import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Renders a keyboard shortcut as styled kbd-chip(s). Auto-detects macOS to
 * show ⌘ vs Ctrl. Pass a single string like "cmd+k", "?", "g p" (chord), or
 * "shift+/", and the component splits + renders each key.
 *
 * Examples:
 *   <KbdShortcut keys="cmd+k" />            → ⌘ K
 *   <KbdShortcut keys="?" />                → ?
 *   <KbdShortcut keys="g p" />              → G then P  (chord, with spacer)
 *   <KbdShortcut keys="shift+/" />          → ⇧ /
 */
const KEY_MAP_MAC = { cmd: '⌘', meta: '⌘', ctrl: '⌃', shift: '⇧', alt: '⌥', option: '⌥', enter: '↵', esc: 'esc', escape: 'esc', tab: 'tab', space: '␣' };
const KEY_MAP_OTHER = { cmd: 'Ctrl', meta: 'Ctrl', ctrl: 'Ctrl', shift: 'Shift', alt: 'Alt', option: 'Alt', enter: 'Enter', esc: 'Esc', escape: 'Esc', tab: 'Tab', space: 'Space' };

const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
const KEY_MAP = isMac ? KEY_MAP_MAC : KEY_MAP_OTHER;

function renderKey(k) {
  const lower = String(k).toLowerCase();
  return KEY_MAP[lower] || k.toUpperCase();
}

export function KbdShortcut({ keys, size = 'small', sx }) {
  // Chord (space-separated, e.g. "g p"): render with " then " between key groups.
  // Combo (+ separated, e.g. "cmd+k"): render with no separator.
  const groups = String(keys || '').trim().split(/\s+/);
  const fontSize = size === 'small' ? 10 : 12;
  const padding = size === 'small' ? '1px 5px' : '2px 7px';

  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ...sx }}>
      {groups.map((group, gi) => (
        <React.Fragment key={gi}>
          {gi > 0 && (
            <Typography component="span" variant="caption" color="text.disabled" sx={{ mx: 0.25, fontSize: fontSize - 1 }}>
              then
            </Typography>
          )}
          {group.split('+').map((k, ki) => (
            <Box
              key={ki}
              component="kbd"
              sx={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: '"SF Mono", "JetBrains Mono", Menlo, monospace',
                fontSize, fontWeight: 600,
                color: 'text.secondary',
                bgcolor: 'grey.100',
                border: '1px solid', borderColor: 'grey.300',
                borderRadius: 0.75, padding,
                minWidth: fontSize + 6, height: fontSize + 8,
                boxShadow: 'inset 0 -1px 0 rgba(0,0,0,0.08)',
              }}
            >
              {renderKey(k)}
            </Box>
          ))}
        </React.Fragment>
      ))}
    </Box>
  );
}

export default KbdShortcut;
